import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  ArrowPathIcon, 
  ArrowDownTrayIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { featureAnnouncementsAPI, usersAPI } from '../services/api';
import { toast } from 'react-toastify';
import moment from 'moment';
import FeatureAnnouncementForm from '../components/FeatureAnnouncementForm';
import ConfirmDialog from '../components/ConfirmDialog';

function FeatureAnnouncementManager() {
  const [announcements, setAnnouncements] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);

  // Date range filter for export/stats
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadAnnouncements(),
        loadOverallAnalytics()
      ]);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadAnnouncements = async () => {
    try {
      const response = await featureAnnouncementsAPI.getAll();
      if (response.data.success) {
        setAnnouncements(response.data.announcements);
      }
    } catch (err) {
      console.error('Error loading announcements:', err);
    }
  };

  const loadOverallAnalytics = async () => {
    try {
      const response = await featureAnnouncementsAPI.getAnalytics();
      if (response.data.success) {
        setAnalytics(response.data.analytics);
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const response = await featureAnnouncementsAPI.toggle(id, !currentStatus);
      if (response.data.success) {
        toast.success(`Feature ${!currentStatus ? 'enabled' : 'disabled'}`);
        loadAnnouncements();
        loadOverallAnalytics();
      }
    } catch (err) {
      toast.error('Failed to toggle status');
    }
  };

  const handleDelete = async () => {
    if (!announcementToDelete) return;
    try {
      const response = await featureAnnouncementsAPI.delete(announcementToDelete.id);
      if (response.data.success) {
        toast.success('Feature announcement deleted');
        loadAnnouncements();
        loadOverallAnalytics();
      }
    } catch (err) {
      toast.error('Failed to delete announcement');
    } finally {
      setIsConfirmOpen(false);
      setAnnouncementToDelete(null);
    }
  };

  const handleExport = async (announcementId) => {
    try {
      toast.info('Preparing export...');
      const response = await featureAnnouncementsAPI.export({
        announcementId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `feature-stats-${announcementId}-${moment().format('YYYYMMDD')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Export successful');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const filteredAnnouncements = announcements.filter(ann => {
    const matchesSearch = ann.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'enabled' && ann.displayAsModal) || 
      (statusFilter === 'disabled' && !ann.displayAsModal);
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Feature Announcements</h1>
          <p className="mt-2 text-sm text-slate-600">
            Manage premium feature modals and track user adoption analytics.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-4 py-2 border border-slate-300 rounded-xl shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 transition-all"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => {
              setSelectedAnnouncement(null);
              setIsFormModalOpen(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-all"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Feature
          </button>
        </div>
      </div>

      {/* Analytics Summary Cards */}
      {analytics && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white overflow-hidden shadow-sm rounded-2xl border border-slate-200 p-6">
            <dt className="text-sm font-medium text-slate-500 truncate">Total Features</dt>
            <dd className="mt-1 text-3xl font-bold text-slate-900">{analytics.totalFeatures}</dd>
            <div className="mt-2 flex items-center text-xs text-indigo-600">
              <EyeIcon className="h-3 w-3 mr-1" />
              <span>{analytics.activeFeatures} currently active</span>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow-sm rounded-2xl border border-slate-200 p-6">
            <dt className="text-sm font-medium text-slate-500 truncate">Total Modal Views</dt>
            <dd className="mt-1 text-3xl font-bold text-slate-900">{analytics.totalViews}</dd>
            <div className="mt-2 flex items-center text-xs text-amber-600">
              <CheckCircleIcon className="h-3 w-3 mr-1" />
              <span>Includes repeat session views</span>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow-sm rounded-2xl border border-slate-200 p-6">
            <dt className="text-sm font-medium text-slate-500 truncate">Avg. Completion Rate</dt>
            <dd className="mt-1 text-3xl font-bold text-slate-900">{analytics.avgCompletionPercentage}%</dd>
            <div className="mt-2 w-full bg-slate-100 rounded-full h-1.5">
              <div 
                className="bg-emerald-500 h-1.5 rounded-full" 
                style={{ width: `${analytics.avgCompletionPercentage}%` }}
              />
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow-sm rounded-2xl border border-slate-200 p-6">
            <dt className="text-sm font-medium text-slate-500 truncate">Permanent Dismissals</dt>
            <dd className="mt-1 text-3xl font-bold text-slate-900">{analytics.totalPermanentDismissals}</dd>
            <div className="mt-2 flex items-center text-xs text-rose-600">
              <XCircleIcon className="h-3 w-3 mr-1" />
              <span>Users opted out of future prompts</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Table */}
      <div className="bg-white shadow-sm rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search features..."
                className="block w-full pl-10 pr-3 py-2 border border-slate-300 rounded-xl leading-5 bg-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                className="block w-full px-3 py-2 text-base border border-slate-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-xl"
              />
              <span className="text-slate-400">-</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                className="block w-full px-3 py-2 text-base border border-slate-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-xl"
              />
            </div>
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-4 w-4 text-slate-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border border-slate-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-xl"
              >
                <option value="all">All Status</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Feature</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Adoption</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Seen / Total</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredAnnouncements.length > 0 ? (
                filteredAnnouncements.map((ann) => (
                  <tr key={ann.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600">
                          <ChartBarIcon className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-bold text-slate-900">{ann.title}</div>
                          <div className="text-xs text-slate-500">Created {moment(ann.createdAt).format('MMM D, YYYY')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleToggleStatus(ann.id, ann.displayAsModal)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${ann.displayAsModal ? 'bg-indigo-600' : 'bg-slate-200'}`}
                      >
                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${ann.displayAsModal ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-center">
                        <span className="text-sm font-bold text-slate-900">{ann.stats?.seenPercentage}%</span>
                        <div className="mt-1 w-24 bg-slate-100 rounded-full h-1.5">
                          <div 
                            className="bg-indigo-500 h-1.5 rounded-full" 
                            style={{ width: `${ann.stats?.seenPercentage}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm text-slate-900 font-medium">
                        {ann.stats?.seenCount} / {ann.stats?.totalUsers}
                      </div>
                      <div className="text-xs text-slate-500">
                        {ann.stats?.dismissedCount} dismissed perm.
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleExport(ann.id)}
                          title="Export CSV"
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAnnouncement(ann);
                            setIsFormModalOpen(true);
                          }}
                          title="Edit"
                          className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            setAnnouncementToDelete(ann);
                            setIsConfirmOpen(true);
                          }}
                          title="Delete"
                          className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <XCircleIcon className="h-12 w-12 text-slate-200 mb-2" />
                      <p className="text-slate-500 font-medium">No announcements found</p>
                      <button 
                        onClick={() => setIsFormModalOpen(true)}
                        className="mt-4 text-indigo-600 font-bold hover:underline"
                      >
                        Create your first one →
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <FeatureAnnouncementForm
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        announcement={selectedAnnouncement}
        onSuccess={() => {
          fetchData();
          setIsFormModalOpen(false);
        }}
      />

      <ConfirmDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete Announcement"
        message={`Are you sure you want to delete "${announcementToDelete?.title}"? This action cannot be undone and all view statistics will be lost.`}
        type="danger"
        confirmText="Delete"
      />
    </div>
  );
}

export default FeatureAnnouncementManager;

